import { FontIcon, Icon, Link, mergeStyles, Stack } from '@fluentui/react';
import * as React from 'react';
import { StyledText } from '../../../../web/common/components/StyledText';
import { getHelpLogger } from '../../../../web/utils/getHelpLogger';
import { LocalizedDaysOfWeek, UserInfo } from '../../../models/apiModels';
import { ChatWeightedId } from '../../../models/constants/constants';
import { ServiceRequestPage, ServiceRequestPageType } from '../../../models/reduxState';
import { isAgentModalityOOBH } from '../../../utilities';
import { WaitTimeResponseModel, WaitTimeResponseReason } from '../models/modalityModels';
import { ExtensionAttributeKeys, FreelanceRequest, RequestModality } from '../models/requestModels';
import { getLinkStyles } from '../styles/commonStyles';
import { getContactUsClassNames, IContactUsStyles } from '../styles/contactUsStyles';
import {
  getModalityIconStyle,
  getModalityLinkStyle,
  getModalitySectionBoxStyle,
  modalityDescriptionStyle,
} from '../styles/modalityStyles';
import { IStyledComponent } from '../styles/themesUI';
import { HoursOfOperation } from './HoursOfOperation';
import { getString } from '../../../../web/utils/stringLoader';
import { collectTelemetry } from '../../../../web/utils/oneDsUtil';
import { LogHint, LogType } from '../../../../web/models/emeraldLoggingRequest';

export interface AgentModalityOptionProps {
  agentModality: WaitTimeResponseModel;
  localizedDaysOfWeek: LocalizedDaysOfWeek;
  alchemyThemeRoutingShowWaitTime: boolean;
  alchemyThemeRoutingShowHOOPs: boolean;
  showModalitiesStickyFooter: boolean;
  draftRequestUserEmail: string;
  userInfo: UserInfo;
  isGroupMe: boolean;
  isOutlookDotCom: boolean;
  upsellModalityId: string;
  isSignUp: boolean;
  isLiveChatEmailRequirementDisabled: boolean;
  isAmc: boolean;
  isGetHelp: boolean;
  alchemyTheme: string;
  isSmbHub: boolean;
  updateDraftRequest: (properties: Partial<FreelanceRequest>) => void;
  navigateToPage: (page: ServiceRequestPage) => void;
  dispatchCreateRequest: () => void;
}

type IAgentModalityOptionProps = AgentModalityOptionProps & IStyledComponent<IContactUsStyles>;

export class AgentModalityOption extends React.Component<IAgentModalityOptionProps, null> {
  public constructor(props: IAgentModalityOptionProps) {
    super(props);
  }

  public render() {
    const contactUsClassNames = getContactUsClassNames(this.props.styles, { theme: this.props.theme });
    const linkStyles = getLinkStyles();
    const modalityName = RequestModality[this.props.agentModality.Modality];
    let modalityTitle = getString(`requestModality_${modalityName}_Title`);
    let icon = null;
    let iconName = null;
    // SMC, GroupMe, Amc, and OutlookDotCom will use descriptive modality labels along with icons
    if (
      this.props.showModalitiesStickyFooter ||
      this.props.isGroupMe ||
      this.props.isOutlookDotCom ||
      this.props.isAmc ||
      this.props.isSmbHub
    ) {
      iconName = this.getModalityIconName();
      icon = !!iconName ? <FontIcon className={contactUsClassNames.requestModalityIcon} iconName={iconName} /> : null;
      modalityTitle = getString(`requestModality_${modalityName}_ExpTitle`, true, true, true) || modalityTitle;
    }

    // VSO 5608280: adding isOutlookDotCom check as a temporary fix until Rave sets up config to handle email correctly for non-english locales, will remove after
    const isOutOfBusinessHours =
      isAgentModalityOOBH(this.props.agentModality) &&
      !(this.props.isOutlookDotCom && this.props.agentModality.Modality == RequestModality.Email);

    if (!this.props.isGetHelp) {
      return (
        <div className={this.getModalityClassName()}>
          {isOutOfBusinessHours ? (
            <div
              className={mergeStyles(
                contactUsClassNames.bodyCopySmall,
                contactUsClassNames.wrapText,
                contactUsClassNames.modalityOptionTitle
              )}
            >
              {icon}
              {getString(`requestModality_${modalityName}_OOBHTitle`)}
            </div>
          ) : (
            <Link
              className={mergeStyles(contactUsClassNames.modalityOptionTitle, contactUsClassNames.wrapText)}
              id={`Agent_Modality_${this.props.agentModality.Modality}`}
              styles={linkStyles}
              tabIndex={0}
              data-bi-bhvr="CONTACT"
              data-bi-area="modalitiesSection"
              data-bi-formnm="maxFunnel"
              data-bi-name={`Agent_Modality_${RequestModality[this.props.agentModality.Modality]}`}
              onClick={this.onModalityClick}
            >
              {icon}
              {modalityTitle}
            </Link>
          )}
          {isOutOfBusinessHours
            ? this.props.alchemyThemeRoutingShowHOOPs && (
                <HoursOfOperation
                  hoursOfOperationText={getString('modality_hoursOfOperationText')}
                  localizedDaysOfWeek={this.props.localizedDaysOfWeek}
                  businessHours={this.props.agentModality.Info.BusinessHours}
                  hoursOfOperationTimeZoneMessage={getString('modality_hoursOfOperationTimeZoneMessage')}
                  theme={this.props.theme}
                />
              )
            : this.props.alchemyThemeRoutingShowWaitTime &&
              !!getString(`requestModality_${modalityName}_Description`, true, true, true) &&
              this.props.agentModality.WaitTime !== null &&
              this.props.agentModality.WaitTime !== undefined && (
                <div>
                  {getString(`requestModality_${modalityName}_Description`).replace(
                    '{0}',
                    this.props.agentModality.WaitTime.toString()
                  )}
                </div>
              )}
        </div>
      );
    }

    const oobhContent = isOutOfBusinessHours && (
      <Stack styles={getModalitySectionBoxStyle(true)}>
        <StyledText styles={modalityDescriptionStyle}>
          {getString(`requestModality_${modalityName}_OOBHTitle`)}
        </StyledText>
        <HoursOfOperation
          hoursOfOperationText={getString('modality_hoursOfOperationText')}
          localizedDaysOfWeek={this.props.localizedDaysOfWeek}
          businessHours={this.props.agentModality.Info.BusinessHours}
          hoursOfOperationTimeZoneMessage={getString('modality_hoursOfOperationTimeZoneMessage')}
          theme={this.props.theme}
        />
      </Stack>
    );

    const normalAgentContent = !isOutOfBusinessHours && (
      <Stack styles={getModalitySectionBoxStyle()}>
        <Stack horizontal tokens={{ childrenGap: 23 }}>
          <Icon iconName={iconName} styles={getModalityIconStyle(this.props.theme.palette.themeDarkAlt)} />
          <Stack>
            <Link
              id={`Agent_Modality_${this.props.agentModality.Modality}`}
              styles={getModalityLinkStyle(this.props.theme.palette.themeDarkAlt)}
              data-bi-bhvr="CONTACT"
              data-bi-area="modalitiesSection"
              data-bi-formnm="maxFunnel"
              data-bi-name={`Agent_Modality_${RequestModality[this.props.agentModality.Modality]}`}
              onClick={this.onModalityClick}
            >
              {modalityTitle}
            </Link>
            {this.props.alchemyThemeRoutingShowWaitTime &&
              !!getString(`requestModality_${modalityName}_Description`, true, true, true) &&
              this.props.agentModality.WaitTime !== null &&
              this.props.agentModality.WaitTime !== undefined && (
                <StyledText>
                  {getString(`requestModality_${modalityName}_Description`).replace(
                    '{0}',
                    this.props.agentModality.WaitTime.toString()
                  )}
                </StyledText>
              )}
          </Stack>
        </Stack>
      </Stack>
    );

    return (
      <>
        {oobhContent}
        {normalAgentContent}
      </>
    );
  }

  private onModalityClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
    if (!!event && !!event.target) {
      event.stopPropagation();
    }
    collectTelemetry({
      ContentId: 'ClickedAgentModality', Type: LogType.PageAction, Hint: LogHint.ClickedAgentModality,
      Details: { 'modality': RequestModality[this.props.agentModality.Modality], 'theme': this.props.alchemyTheme }
    });
    getHelpLogger.logAgentModalityClicked(RequestModality[this.props.agentModality.Modality], this.props.alchemyTheme);
    const properties: Partial<FreelanceRequest> = { Modality: this.props.agentModality.Modality };
    if (this.props.agentModality.WaitTime !== null && this.props.agentModality.WaitTime !== undefined) {
      properties.ExtensionAttributes = {
        [ExtensionAttributeKeys.WaitTime]: this.props.agentModality.WaitTime.toString(),
      };
    } else {
      properties.ExtensionAttributes = { [ExtensionAttributeKeys.WaitTime]: null };
    }
    // Set UserEmail on modality click instead of userInfoSuccess.  Since C2C ticket creation resets UserEmail, we have to repopulate it for
    // scenario where user encounters C2C ticket failure, gets sent back to modality stage, and then selects chat/email.
    // If UserEmail is '', then that means it was left intentionally blank and should not be reset with userInfo (ex: user deletes email in field, click back, then click email again)
    // If UserEmail is null/undefined, then it should be automatically populated if userInfo exists, otherwise null and await user input.
    // SignUp: Don't ask for UserEmail until it passes the privacy review.
    if (
      (this.props.agentModality.Modality == RequestModality.Chat && !this.props.isLiveChatEmailRequirementDisabled) ||
      this.props.agentModality.Modality == RequestModality.Email
    ) {
      properties.UserEmail =
        !!this.props.draftRequestUserEmail || this.props.draftRequestUserEmail === ''
          ? this.props.draftRequestUserEmail
          : !!this.props.userInfo
          ? this.props.userInfo.MemberName
          : null;
    }
    this.props.updateDraftRequest(properties);
    if (
      this.props.agentModality.Modality == RequestModality.Chat &&
      (this.props.isLiveChatEmailRequirementDisabled || this.props.isSmbHub)
    ) {
      this.props.dispatchCreateRequest();
      const page: ServiceRequestPage = {
        isBackButtonPresent: false,
        serviceRequestPageType: ServiceRequestPageType.RequestConfirmation,
      };
      this.props.navigateToPage(page);
    } else {
      const page: ServiceRequestPage = {
        isBackButtonPresent: true,
        serviceRequestPageType: ServiceRequestPageType.RequestCreation,
      };
      this.props.navigateToPage(page);
    }
  };

  private getModalityIconName = (): string => {
    if (!!this.props.agentModality) {
      switch (this.props.agentModality.Modality) {
        case RequestModality.Chat:
          return 'Message';
        case RequestModality.C2C:
        case RequestModality.Phone:
          return 'Phone';
        case RequestModality.Email:
          return 'Mail';
        default:
          return '';
      }
    }
    return '';
  };

  private getModalityClassName = (): string => {
    const contactUsClassNames = getContactUsClassNames(this.props.styles, { theme: this.props.theme });
    let baseClass = mergeStyles('col-lg-12', contactUsClassNames.col, contactUsClassNames.modalityOption);
    baseClass = this.props.isSignUp ? baseClass : mergeStyles(contactUsClassNames.bodyCopySmall, baseClass);
    const upsellClass = this.getUpsellClassName();
    return mergeStyles(upsellClass, baseClass);
  };

  private getUpsellClassName = (): string => {
    const contactUsClassNames = getContactUsClassNames(this.props.styles, { theme: this.props.theme });
    let upsellClass = '';
    if (!!this.props.upsellModalityId) {
      upsellClass = contactUsClassNames.upsellScenario;
      if (
        this.props.upsellModalityId.indexOf(ChatWeightedId) !== -1 &&
        this.props.agentModality.Modality == RequestModality.Chat &&
        !(
          this.props.agentModality.Reason == WaitTimeResponseReason.OutOfBusinessHours ||
          this.props.agentModality.Reason == WaitTimeResponseReason.SpecialistOutOfBusinessHours
        )
      ) {
        upsellClass = mergeStyles(contactUsClassNames.upsellChatWeighted, upsellClass);
      }
    }
    return upsellClass;
  };
}
